import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import styles from './InfoPopover.module.css';

interface Props {
  content: ReactNode;
  placement?: 'top' | 'bottom';
  label?: string;
  className?: string;
}

const CLOSE_DELAY_MS = 100;
const VIEWPORT_PADDING = 8;

type Align = 'left' | 'right' | 'center';

export function InfoPopover({ content, placement = 'bottom', label = 'More info', className }: Props) {
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState<Align>('left');
  const ref = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<number | null>(null);
  const id = useId();

  const cancelClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelClose(), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Viewport-aware horizontal alignment. Default left-anchored; if that
  // would overflow the right edge of the viewport, flip to right-anchored.
  // If even right-anchored doesn't fit (very narrow phone, very wide
  // popover), center it as a last resort.
  useLayoutEffect(() => {
    if (!open || !popoverRef.current || !ref.current) return;
    const triggerRect = ref.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const wouldOverflowRight = triggerRect.left + popoverRect.width + VIEWPORT_PADDING > vw;
    const wouldOverflowLeft = triggerRect.right - popoverRect.width - VIEWPORT_PADDING < 0;

    if (wouldOverflowRight && !wouldOverflowLeft) setAlign('right');
    else if (wouldOverflowRight && wouldOverflowLeft) setAlign('center');
    else setAlign('left');
  }, [open]);

  return (
    <span
      ref={ref}
      className={[styles.wrap, className].filter(Boolean).join(' ')}
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      onFocus={() => { cancelClose(); setOpen(true); }}
      onBlur={scheduleClose}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        // Click toggles for touch / keyboard users where hover doesn't apply.
        onClick={() => setOpen((o) => !o)}
      >
        <Info size={14} strokeWidth={1.5} />
      </button>
      {open && (
        <span
          ref={popoverRef}
          id={id}
          role="tooltip"
          className={[
            styles.popover,
            placement === 'top' ? styles.top : styles.bottom,
            align === 'right' ? styles.alignRight : align === 'center' ? styles.alignCenter : styles.alignLeft,
          ].join(' ')}
        >
          {content}
        </span>
      )}
    </span>
  );
}
